import type { ApiResponse, MentionStats } from '@fluenceur/types';
import type { UseQueryResult } from '@tanstack/react-query';
import { queryOptions, useQuery } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';

/**
 * The backend refuses a stats request naming more mentions than this
 * (`ListMentionStatsController`). A caller holding more ids splits them.
 */
export const MENTION_STATS_MAX_IDS_PER_REQUEST = 20;

/**
 * Fetch mention stats for multiple mentions
 * @param mentionIds - Array of mention IDs
 * @returns Promise with mention stats data
 */
const fetchMentionStats = async (mentionIds: string[]): Promise<MentionStats[]> => {
  const response = await api.get<ApiResponse<MentionStats[]>>(`/dashboard/mentions/stats`, {
    params: { ids: mentionIds.join(',') },
  });
  return response.data.data as MentionStats[];
};

const MENTION_STATS_QUERY_KEY_PREFIX = 'mention-stats';

/** Query options for one stats request; `mentionIds` must fit in a single request. */
export const mentionStatsQueryOptions = (mentionIds: string[], enabled = true) =>
  queryOptions<MentionStats[], Error>({
    queryKey: [MENTION_STATS_QUERY_KEY_PREFIX, mentionIds.join(',')],
    queryFn: () => {
      if (mentionIds.length === 0) {
        return Promise.reject(new Error('At least one mention ID is required.'));
      }
      return fetchMentionStats(mentionIds);
    },
    enabled: mentionIds.length > 0 && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });

interface UseMentionStatsQueryOptions {
  mentionIds: string[];
  enabled?: boolean;
}

export const useMentionStatsQuery = ({
  mentionIds,
  enabled = true,
}: UseMentionStatsQueryOptions): UseQueryResult<MentionStats[], Error> =>
  useQuery(mentionStatsQueryOptions(mentionIds, enabled));

// Export the fetch function for use in other hooks
export { fetchMentionStats };
